import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { useBlocker, useNavigate } from "react-router-dom";
import { notification } from "antd";
import { IpSetCreateContent } from "./content";
import { getDefaultIpSetInput, inputToIpSet } from "../utils";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { sentryLog } from "../../../tools/sentry";
import { getValidator } from "../validator";
import { IpSetInput } from "../../../schema/types/ipset/input";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { ipSetApi } from "../../../store/api/ipSetApi";

interface Props {}
export const IpSetCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultIpSetInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/ipset/-create`) &&
      !nextLocation.pathname.includes(`/ipset/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: IpSetInput) {
    try {
      let body = inputToIpSet(input);
      const method = "post";
      const url = homeLink("ipset");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("ipset", body.name);
      dispatch(ipSetApi.util.invalidateTags([{ type: "IpSet", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Ip Set is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <IpSetCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToIpSet} kind="ipset" namePath="name" title="Ip Set" />
      <CpDebugModal inputToItemFn={inputToIpSet} kind="ipset" namePath="name" title="Ip Set" />
    </CpFormContext.Provider>
  );
};
